import React, { useState } from 'react';
import { FileText, Download, Trash2, RotateCcw, FolderInput, Pencil, Check, X } from 'lucide-react';
import StatusBadge from './StatusBadge';
import { DOC_CATEGORIES } from '../../data/mockData';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_API_URL || 'https://api.orbit-technology.com/api';

function fmt(iso) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

async function downloadDoc(doc) {
  if (!doc.key && !doc.id) {
    alert('Download not available — file not yet stored in S3');
    return;
  }
  try {
    const res = await fetch(`${API}/documents/download/${doc.id}`);
    if (!res.ok) throw new Error('Server error');
    const data = await res.json();
    if (!data.url) throw new Error('No URL returned');
    window.open(data.url, '_blank');
  } catch (err) {
    console.error('[Download]', err);
    alert('Download failed — file may have been removed from storage. Try re-uploading.');
  }
}

export default function DocumentTable({ documents, onStatusChange, canChangeStatus = false, onDelete, onMove, onRename }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  // No auth middleware runs on the documents router, so the server can't read
  // req.user for these actions - sent explicitly instead, the same way
  // uploads already carry their own uploadedBy, so the Audit Trail can say
  // who moved/deleted/restored a document instead of just "Unknown".
  const performedBy = user?.full_name || user?.email || 'Unknown';
  const [deletedDocs, setDeletedDocs] = useState([]);
  const [permDeletedIds, setPermDeletedIds] = useState(new Set());
  const [showDeleted, setShowDeleted] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [permDeleteConfirm, setPermDeleteConfirm] = useState(null);
  const [moveMenuFor, setMoveMenuFor] = useState(null);
  const [moveMenuPos, setMoveMenuPos] = useState(null); // viewport coords, so the menu can't get clipped by an ancestor's overflow
  const [movingId, setMovingId] = useState(null);
  // Renaming is offered only where the page passes onRename (the rep side) -
  // the client portal uses this same table and must not get it.
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);

  // The extension is shown beside the box rather than in it, so it cannot be
  // typed over - the server keeps it either way.
  const splitExt = (name = '') => {
    const m = name.match(/\.[A-Za-z0-9]{1,5}$/);
    return m ? [name.slice(0, -m[0].length), m[0]] : [name, ''];
  };
  const startRename = (doc) => { setRenamingId(doc.id); setRenameValue(splitExt(doc.fileName)[0]); };
  const cancelRename = () => { setRenamingId(null); setRenameValue(''); };
  const saveRename = async (doc) => {
    if (renameSaving) return;
    const [base, ext] = splitExt(doc.fileName);
    const next = renameValue.trim();
    if (!next || next === base) { cancelRename(); return; }
    setRenameSaving(true);
    try {
      const res = await fetch(`${API}/documents/${doc.id}/rename`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: next + ext, performedBy }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Server error');
      if (onRename) onRename(doc.id, d.fileName || next + ext);
      cancelRename();
    } catch (err) {
      console.error('[Rename]', err);
      alert(`Could not rename that document — ${err.message || 'please try again.'}`);
    } finally {
      setRenameSaving(false);
    }
  };

  const openMoveMenu = (e, doc) => {
    if (moveMenuFor === doc.id) { setMoveMenuFor(null); setMoveMenuPos(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 192;  // w-48
    const menuHeight = 320; // rough max height for up to 9 categories
    const openUpward = window.innerHeight - rect.bottom < menuHeight;
    setMoveMenuPos({
      left: Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8),
      top: openUpward ? rect.top - menuHeight - 4 : rect.bottom + 4,
    });
    setMoveMenuFor(doc.id);
  };

  const handleMove = async (doc, categoryId) => {
    setMoveMenuFor(null);
    setMovingId(doc.id);
    try {
      const res = await fetch(`${API}/documents/${doc.id}/category`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: categoryId, performedBy }),
      });
      if (!res.ok) throw new Error('Server error');
      if (onMove) onMove(doc.id, categoryId);
    } catch (err) {
      console.error('[Move]', err);
      alert('Failed to move document — please try again.');
    } finally {
      setMovingId(null);
    }
  };

  const activeDocs = documents.filter(d => !deletedDocs.find(dd => dd.id === d.id) && !permDeletedIds.has(d.id));

  // Deleting moves the document to the Trash folder.
  //
  // This used to be a local list only: the row vanished from the table and came
  // straight back on refresh, because nothing was told about it. It now calls
  // the server, so the document really is in Trash and stays there - and the
  // file itself is untouched in S3 either way.
  const handleSoftDelete = async (doc) => {
    try {
      const res = await fetch(`${API}/documents/${doc.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ performedBy }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Server error');
      }
    } catch (err) {
      console.error('[Trash]', err);
      alert('Could not move that document to Trash — please try again.');
      setDeleteConfirm(null);
      return;
    }
    setDeletedDocs(prev => [...prev, doc]);
    if (onDelete) onDelete(doc.id, 'soft');
    setDeleteConfirm(null);
  };

  // Restore puts it back in whichever folder it was deleted from - the server
  // recorded that when it was trashed, so nobody has to remember.
  const handleRestore = async (doc) => {
    try {
      const res = await fetch(`${API}/documents/${doc.id}/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ performedBy }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Server error');
      }
    } catch (err) {
      console.error('[Restore]', err);
      alert('Could not restore that document — please try again.');
      return;
    }
    setDeletedDocs(prev => prev.filter(d => d.id !== doc.id));
    if (onDelete) onDelete(doc.id, 'restored');
  };

  // The only path that actually destroys anything. It refuses unless the
  // document is already in Trash, so a permanent delete is always a second,
  // separate decision rather than one misplaced click.
  const handlePermanentDelete = async (doc) => {
    try {
      const res = await fetch(`${API}/documents/${doc.id}/permanent`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ performedBy }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Server error');
      }
    } catch (err) {
      console.error('[PermanentDelete]', err);
      alert('Could not permanently delete that document — please try again.');
      setPermDeleteConfirm(null);
      return;
    }
    setDeletedDocs(prev => prev.filter(d => d.id !== doc.id));
    setPermDeletedIds(prev => new Set([...prev, doc.id]));
    if (onDelete) onDelete(doc.id, 'permanent');
    setPermDeleteConfirm(null);
  };

  const getCategoryLabel = (id) => DOC_CATEGORIES.find(c => c.id === id)?.label || id;

  if (!activeDocs.length && !deletedDocs.length) {
    return (
      <div className="text-center py-12">
        <FileText size={24} className="mx-auto text-gray-300 mb-3" />
        <p className="text-gray-400 text-sm">{t('No files in this category yet')}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-sm p-6">
            <h2 className="text-gray-900 font-semibold text-base mb-2">Delete Document?</h2>
            <p className="text-gray-500 text-sm mb-1">Are you sure you want to delete <span className="font-semibold text-gray-700">{deleteConfirm.fileName}</span>?</p>
            <p className="text-gray-400 text-xs mb-5">You can restore it from the deleted section below.</p>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setDeleteConfirm(null)} className="text-gray-500 text-sm px-4 py-2 rounded-xl hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleSoftDelete(deleteConfirm)} className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-sm px-4 py-2 rounded-xl font-medium">
                <Trash2 size={13} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permanent delete confirmation */}
      {permDeleteConfirm && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl border border-red-200 w-full max-w-sm p-6">
            <h2 className="text-red-600 font-semibold text-base mb-2">Permanently Delete?</h2>
            <p className="text-gray-500 text-sm mb-1">This will permanently remove <span className="font-semibold text-gray-700">{permDeleteConfirm.fileName}</span> from S3.</p>
            <p className="text-red-400 text-xs mb-5">This action cannot be undone.</p>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setPermDeleteConfirm(null)} className="text-gray-500 text-sm px-4 py-2 rounded-xl hover:bg-gray-50">Cancel</button>
              <button onClick={() => handlePermanentDelete(permDeleteConfirm)} className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-sm px-4 py-2 rounded-xl font-medium">
                <Trash2 size={13} /> Permanently Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active documents table */}
      {activeDocs.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-3 px-5 text-gray-400 font-medium text-xs uppercase tracking-wide">File</th>
                <th className="text-left py-3 px-5 text-gray-400 font-medium text-xs uppercase tracking-wide">Category</th>
                <th className="text-left py-3 px-5 text-gray-400 font-medium text-xs uppercase tracking-wide">Status</th>
                <th className="text-left py-3 px-5 text-gray-400 font-medium text-xs uppercase tracking-wide">Uploaded</th>
                <th className="text-left py-3 px-5 text-gray-400 font-medium text-xs uppercase tracking-wide">Size</th>
                <th className="text-right py-3 px-5"></th>
              </tr>
            </thead>
            <tbody>
              {activeDocs.map(doc => (
                <tr key={doc.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors group">
                  <td className="py-3.5 px-5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                        <FileText size={14} className="text-blue-500" />
                      </div>
                      {renamingId === doc.id ? (
                        <div className="flex items-center gap-1">
                          <input
                            autoFocus
                            value={renameValue}
                            disabled={renameSaving}
                            maxLength={140}
                            onChange={e => setRenameValue(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') saveRename(doc); if (e.key === 'Escape') cancelRename(); }}
                            className="w-44 border border-gray-200 rounded-lg px-2 py-1 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          />
                          <span className="text-gray-400 text-xs">{splitExt(doc.fileName)[1]}</span>
                          <button title="Save" disabled={renameSaving} onClick={() => saveRename(doc)} className="p-1.5 hover:bg-green-50 rounded-lg text-green-600 disabled:opacity-50 transition-colors"><Check size={13} /></button>
                          <button title="Cancel" disabled={renameSaving} onClick={cancelRename} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 disabled:opacity-50 transition-colors"><X size={13} /></button>
                        </div>
                      ) : (
                      <div>
                        <p className="text-gray-800 font-medium truncate max-w-[180px]" title={doc.fileName}>{doc.fileName}</p>
                        {doc.note && <p className="text-gray-400 text-xs mt-0.5 truncate max-w-[180px]">{doc.note}</p>}
                      </div>
                      )}
                      {doc.visibility === 'internal' && (
                        <span className="text-xs bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded">Internal</span>
                      )}
                    </div>
                  </td>
                  <td className="py-3.5 px-5 text-gray-400 text-xs">{getCategoryLabel(doc.category)}</td>
                  <td className="py-3.5 px-5">
                    {canChangeStatus && onStatusChange ? (
                      <select
                        value={doc.status}
                        onChange={e => onStatusChange(doc.id, e.target.value)}
                        className="bg-white border border-gray-200 text-gray-700 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      >
                        {['Uploaded','Under Review','Approved','Rejected','Needs Reupload'].map(s => (
                          <option key={s}>{s}</option>
                        ))}
                      </select>
                    ) : (
                      <StatusBadge status={doc.status} size="xs" />
                    )}
                  </td>
                  <td className="py-3.5 px-5 text-gray-400 text-xs">{fmt(doc.uploadedAt)}</td>
                  <td className="py-3.5 px-5 text-gray-400 text-xs">{doc.fileSize}</td>
                  <td className="py-3.5 px-5">
                    <div className="relative flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => downloadDoc(doc)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-700 transition-colors"><Download size={13} /></button>
                      {onRename && (
                        <button title="Rename" onClick={() => startRename(doc)} className="p-1.5 hover:bg-blue-50 rounded-lg text-gray-400 hover:text-blue-500 transition-colors"><Pencil size={13} /></button>
                      )}
                      <button onClick={() => setDeleteConfirm(doc)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-500 transition-colors"><Trash2 size={13} /></button>
                      <button
                        title="Move to"
                        disabled={movingId === doc.id}
                        onClick={(e) => openMoveMenu(e, doc)}
                        className="p-1.5 hover:bg-blue-50 rounded-lg text-gray-400 hover:text-blue-500 disabled:opacity-50 transition-colors"
                      >
                        <FolderInput size={13} />
                      </button>
                      {moveMenuFor === doc.id && moveMenuPos && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => { setMoveMenuFor(null); setMoveMenuPos(null); }} />
                          <div
                            style={{ position: 'fixed', top: moveMenuPos.top, left: moveMenuPos.left }}
                            className="z-50 bg-white border border-gray-200 rounded-xl shadow-lg py-1 w-48"
                          >
                            <p className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Move to</p>
                            {DOC_CATEGORIES.filter(c => c.id !== doc.category).map(c => (
                              <button
                                key={c.id}
                                onClick={() => { handleMove(doc, c.id); setMoveMenuPos(null); }}
                                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-left"
                              >
                                <span>{c.icon}</span> {c.label}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Deleted documents section */}
      {deletedDocs.length > 0 && (
        <div className="border-t border-gray-100">
          <button onClick={() => setShowDeleted(v => !v)} className="flex items-center gap-2 text-gray-400 text-xs hover:text-gray-600 transition-colors px-5 py-3 w-full text-left">
            <Trash2 size={12} />
            Deleted ({deletedDocs.length})
            <span>{showDeleted ? '▲' : '▼'}</span>
          </button>
          {showDeleted && (
            <div className="divide-y divide-red-50">
              {deletedDocs.map(doc => (
                <div key={doc.id} className="flex items-center justify-between px-5 py-3 bg-red-50/30">
                  <div className="flex items-center gap-2.5">
                    <FileText size={13} className="text-gray-400" />
                    <span className="text-gray-500 text-sm">{doc.fileName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleRestore(doc)} className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 border border-green-200 hover:border-green-300 px-2.5 py-1.5 rounded-lg transition-colors">
                      <RotateCcw size={11} /> Restore
                    </button>
                    <button onClick={() => setPermDeleteConfirm(doc)} className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600 border border-red-200 hover:border-red-300 px-2.5 py-1.5 rounded-lg transition-colors">
                      <Trash2 size={11} /> Delete Forever
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
